import apiService from './api';
import type { ApiResponse } from '../types';
import type { CultivarRightsState } from '../types/cultivar';

/**
 * FactreeTech's review of claims to the rights in protected cultivars, and of
 * organisations that say they license for a breeder not on the platform.
 * Platform super admin only (HHNodeServer `/api/platform/cultivar-claims`,
 * cultivarRightsService). The server decides what is pending; this page lists
 * and records decisions.
 */

export type ClaimDecision = 'verified' | 'rejected';
export type ClaimListState = 'pending' | 'verified' | 'rejected';

export interface ClaimProtection {
  id: number;
  jurisdiction: string;
  type: string;
  status: string;
  registration_number: string | null;
  grant_date: string | null;
  expiry_date: string | null;
  authority: string | null;
  source_url: string | null;
}

export interface CultivarClaim {
  id: number;
  name: string;
  trade_name: string | null;
  protection_status: string;
  rights_state: CultivarRightsState;
  breeder_name: string | null;
  breeder_org_id: number | null;
  breeder_org_name: string | null;
  breeder_claim_state: string | null;
  org_id: number | null;
  org_name: string | null;
  crop_type_name: string | null;
  updated_at: string;
  rights_reviewed_at: string | null;
  rights_review_note: string | null;
  reviewed_by_name: string | null;
  protections: ClaimProtection[];
  licensor_names: string[];
  live_licences: number;
  /** Other cultivars on the platform claiming one of the same registration numbers. */
  same_number_elsewhere: {
    cultivar_id: number;
    name: string;
    org_name: string | null;
    registration_number: string;
    rights_state: CultivarRightsState;
  }[];
}

export interface RegistryCheckResult {
  jurisdiction: string;
  type: string;
  registration_number: string | null;
  checked: boolean;
  found?: boolean;
  denomination?: string | null;
  applicant?: string | null;
  registry_status?: string | null;
  /** Whether the registry's variety name is the cultivar's name or trade name. */
  name_matches?: boolean | null;
  dataset?: string;
  message?: string;
}

export interface LicensorClaim {
  id: number;
  region_code: string;
  scope: 'all_cultivars' | 'specific';
  state: string;
  claimed_at: string;
  notes: string | null;
  platform_review_state: 'pending' | 'verified' | 'rejected';
  platform_reviewed_at: string | null;
  platform_review_note: string | null;
  breeder_org_id: number;
  breeder_org_name: string;
  breeder_claim_state: string | null;
  breeder_country: string | null;
  breeder_website: string | null;
  licensor_org_id: number;
  licensor_org_name: string;
  claimed_by_name: string | null;
  cultivar_names: string[];
}

export interface ClaimsSummary {
  cultivars: number;
  licensors: number;
}

export interface DecideResult {
  decided: { id: number; name: string }[];
  refused: { id: number; name?: string; reason: string }[];
}

class CultivarClaimsService {
  async summary(): Promise<ApiResponse<ClaimsSummary>> {
    return apiService.get('/platform/cultivar-claims/summary');
  }

  async list(state: ClaimListState = 'pending'): Promise<ApiResponse<CultivarClaim[]>> {
    return apiService.get(`/platform/cultivar-claims?state=${state}`);
  }

  async registryCheck(cultivarId: number): Promise<ApiResponse<{ cultivar_id: number; results: RegistryCheckResult[] }>> {
    // IP Australia's dataset can be slow; the default 10 s is not enough.
    return apiService.get(`/platform/cultivar-claims/${cultivarId}/registry-check`, { timeout: 60000 });
  }

  async decide(ids: number[], decision: ClaimDecision, note?: string): Promise<ApiResponse<DecideResult>> {
    return apiService.post('/platform/cultivar-claims/decide', { ids, decision, note: note || null });
  }

  async listLicensors(state: ClaimListState = 'pending'): Promise<ApiResponse<LicensorClaim[]>> {
    return apiService.get(`/platform/licensor-claims?state=${state}`);
  }

  async decideLicensors(ids: number[], decision: ClaimDecision, note?: string): Promise<ApiResponse<{ decided: number[] }>> {
    return apiService.post('/platform/licensor-claims/decide', { ids, decision, note: note || null });
  }
}

export default new CultivarClaimsService();
